import { useEffect, useRef, useState } from 'react';
import { bylineName, bylineLink } from '@/lib/byline';
import { BylineWrap } from './BylineWrap';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, MessageSquare, Bookmark, ExternalLink, Play, Link2, Check } from 'lucide-react';
import { getCategoryIcon } from '@/lib/category-icon';
import { resolvePostHero } from '@/lib/post-hero';
import { outboundUrl } from '@/lib/post-origin';
import { api, type Post } from '@/lib/api';
import { trackBookmarkAction, trackBookmarkInteraction, trackFeedInteraction, trackShare, trackShareButtonClick, trackVideoCardImpression, trackVideoCardClick, trackVideoPlayStarted } from '@/lib/analytics';
import { ogShareUrl } from '@/components/post/ShareBar';
import { RelativeTime } from '@/components/RelativeTime';
import { usePushOptIn } from '@/hooks/usePushOptIn';

interface PostCardGridProps {
  post: Post;
}


// Check if a thumbnail URL is likely a placeholder/low-quality image
export function PostCardGrid({ post }: PostCardGridProps) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const videoGridRef = useRef<HTMLDivElement>(null);
  // GROWTH-003: Push opt-in on first save action
  const { maybePromptPush } = usePushOptIn();

  // ANALYTICS-090: Video card impression — one-shot on viewport entry (grid view)
  const isVideo = post.contentType === 'video';
  useEffect(() => {
    const el = videoGridRef.current;
    if (!el || !isVideo) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackVideoCardImpression(post.id, post.category, post.duration ?? null, 'feed_grid');
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.category, post.duration, isVideo]);

  // UI-089: Direct WhatsApp share URL (uses og share endpoint for UTM tracking)
  const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this on MotoWalla: ${post.title} ${ogShareUrl(post.id, 'whatsapp')}`)}`;

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(ogShareUrl(post.id, 'copy_link'));
      setCopied(true);
      trackShare('copy', post.id, post.title, post.category, post.source?.id);
      trackShareButtonClick(post.id, 'copy', 'feed_grid');
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // silently fail
    }
  };

  const upvoteMutation = useMutation({
    mutationFn: () => api.posts.upvote(post.id),
    onSuccess: () => {
      trackFeedInteraction('upvote', post.id, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown');
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  const downvoteMutation = useMutation({
    mutationFn: () => api.posts.downvote(post.id),
    onSuccess: () => {
      trackFeedInteraction('downvote', post.id, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown');
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  const bookmarkMutation = useMutation({
    mutationFn: () => api.posts.bookmark(post.id),
    onSuccess: () => {
      const wasBookmarked = post.userInteraction?.bookmarked;
      trackBookmarkAction(
        wasBookmarked ? 'bookmark_removed' : 'bookmark_added',
        post.id,
        post.category,
        'feed_grid',
      );
      // ANALYTICS-027: Bookmark interaction with vehicle metadata
      trackBookmarkInteraction(
        wasBookmarked ? 'bookmark_removed' : 'bookmark_added',
        post.id,
        post.tags,
        post.category,
        post.vehicleTypes,
        post.aiEntities?.price_range,
        'feed_grid',
      );
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      // GROWTH-003: Prompt push opt-in on first bookmark
      if (!wasBookmarked) maybePromptPush();
    },
  });

  const isUpvoted = post.userInteraction?.upvoted;
  const isDownvoted = post.userInteraction?.downvoted;
  const isBookmarked = post.userInteraction?.bookmarked;
  // T-249: same rule as PostCard — a card gets a real thumbnail or the icon,
  // never a stock photo and never a title card. isValidThumbnail now lives
  // inside the shared resolver, which is how the other two files got it.
  const hero = resolvePostHero(post);
  const [heroFailed, setHeroFailed] = useState(false);
  // T-179: see lib/category-icon.ts — a shared exhaustive map, because the
  // ternary here fell through to Car for scooters, ADV and motorsport.
  const CategoryIcon = getCategoryIcon(post.category);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // T-128: null when this byline has no real destination (see lib/byline).
  const bylineHref = bylineLink(post);

  const voteScore = post.upvoteCount - post.downvoteCount;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border-primary/80 bg-gradient-to-b from-bg-secondary via-bg-secondary to-bg-tertiary/30 backdrop-blur-sm transition-all duration-300 ease-out hover:border-primary-500/50 hover:shadow-2xl hover:shadow-primary-500/15 hover:-translate-y-1">
      {/* Source Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        {/* T-128: see PostCard — no '#' affordance, decision 105. */}
        <BylineWrap
          href={bylineHref}
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 flex items-center gap-2 rounded-lg hover:bg-bg-tertiary -ml-1 px-1 py-0.5 transition-colors"
        >
          {post.source?.logoUrl ? (
            <img
              src={post.source.logoUrl}
              alt=""
              className="h-6 w-6 rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-xs font-bold text-white">
              {post.source?.name?.charAt(0) || 'M'}
            </div>
          )}
          <span className="text-sm font-semibold text-text-secondary truncate hover:text-primary-400 transition-colors duration-200 hover:underline hover:underline-offset-2">
            {bylineName(post)}
          </span>
        </BylineWrap>
        <span className="text-text-tertiary">·</span>
        <RelativeTime date={post.publishedAt} className="text-xs text-text-tertiary" />
      </div>

      {/* Title - fixed height for 2 lines with line-clamp */}
      <h3 className="px-4 pb-2.5 text-[16px] font-semibold leading-snug tracking-tight text-text-primary line-clamp-2 h-[3.25rem] group-hover:text-primary-400 transition-colors duration-200">
        <Link to={`/post/${post.id}`} className="after:absolute after:inset-0" onClick={() => trackFeedInteraction('click', post.id, post.category, post.source?.url || 'unknown', post.source?.name || 'unknown')}>
          {post.title}
        </Link>
      </h3>

      {/* Tags Row */}
      <div className="flex items-center gap-1.5 px-4 pb-3">
        {/* Category Badge */}
        <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
          post.category === 'motorcycles'
            ? 'bg-gradient-to-r from-cat-motorcycles/20 to-cat-motorcycles/10 text-cat-motorcycles ring-1 ring-cat-motorcycles/30'
            : post.category === 'ev'
            ? 'bg-gradient-to-r from-green-500/20 to-green-500/10 text-green-400 ring-1 ring-green-500/30'
            : 'bg-gradient-to-r from-cat-cars/20 to-cat-cars/10 text-cat-cars ring-1 ring-cat-cars/30'
        }`}>
          <CategoryIcon className="h-3 w-3" />
          {post.category === 'motorcycles' ? 'Moto' : post.category === 'ev' ? 'EV' : 'Auto'}
        </span>

        {/* Brand Tags */}
        {post.brands?.slice(0, 2).map((brand) => (
          <span
            key={brand}
            className="inline-flex items-center rounded-md bg-gradient-to-r from-primary-500/15 to-primary-500/5 px-2 py-0.5 text-[10px] font-semibold text-primary-400 ring-1 ring-primary-500/20 transition-all duration-200 hover:ring-primary-500/40 cursor-pointer"
          >
            {brand}
          </span>
        ))}

        {/* Overflow indicator */}
        {(post.brands?.length || 0) + (post.tags?.length || 0) > 2 && (
          <span className="text-[10px] font-semibold text-text-tertiary bg-bg-tertiary/50 px-1.5 py-0.5 rounded">
            +{(post.brands?.length || 0) + (post.tags?.length || 0) - 2}
          </span>
        )}
      </div>

      {/* Thumbnail */}
      <div ref={videoGridRef} className="relative mx-4 mb-3 aspect-[16/10] overflow-hidden rounded-xl bg-bg-tertiary ring-1 ring-white/5 group-hover:ring-primary-500/20 transition-all duration-300">
        {hero && !heroFailed ? (
          <img
            src={hero.src}
            alt={hero.alt}
            className="h-full w-full object-cover transition-all duration-500 ease-out group-hover:scale-110 group-hover:brightness-110"
            onError={() => setHeroFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <CategoryIcon className="h-10 w-10 text-text-tertiary" />
          </div>
        )}

        {/* Video overlay */}
        {post.contentType === 'video' && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent transition-opacity duration-300" />
            <div
              className="absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100"
              onClick={() => {
                trackVideoCardClick(post.id, post.category, post.duration ?? null, 'feed_grid');
                trackVideoPlayStarted(post.id, post.category, post.duration ?? null, 'feed_grid');
              }}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-2xl shadow-black/40 ring-4 ring-white/20 transition-transform duration-300 group-hover:scale-105 backdrop-blur-sm">
                <Play className="h-7 w-7 text-gray-900 ml-1 drop-shadow-sm" fill="currentColor" />
              </div>
            </div>
            {post.duration && (
              <div className="absolute bottom-2 right-2 rounded-md bg-black/85 px-2 py-0.5 text-xs font-bold text-white backdrop-blur-sm shadow-lg tabular-nums">
                {formatDuration(post.duration)}
              </div>
            )}
          </>
        )}

        {/* Read time for articles */}
        {post.contentType === 'article' && post.readTime && (
          <div className="absolute bottom-2 right-2 rounded-md bg-black/85 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm shadow-lg">
            {post.readTime}m read
          </div>
        )}

        {/* Inner glow ring */}
        <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-white/10 pointer-events-none" />
      </div>

      {/* Actions Footer */}
      <div className="flex items-center gap-1 border-t border-border-primary/50 px-3 py-2.5 mt-auto bg-gradient-to-t from-bg-tertiary/20 to-transparent">
        {/* Vote buttons */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            upvoteMutation.mutate();
          }}
          className={`relative z-10 flex items-center rounded-lg p-1.5 transition-all duration-200 ${
            isUpvoted
              ? 'text-upvote bg-upvote/15 shadow-sm shadow-upvote/20'
              : 'text-text-tertiary hover:text-upvote hover:bg-upvote/10 hover:-translate-y-0.5 active:translate-y-0'
          }`}
        >
          <ArrowUp className={`h-4 w-4 transition-transform duration-200 ${isUpvoted ? 'fill-current scale-110' : ''}`} />
        </button>

        <span className={`min-w-[2rem] text-center text-xs font-bold tabular-nums transition-all duration-200 ${
          isUpvoted ? 'text-upvote scale-110' : isDownvoted ? 'text-downvote scale-110' : 'text-text-primary'
        }`}>
          {voteScore}
        </span>

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            downvoteMutation.mutate();
          }}
          className={`relative z-10 flex items-center rounded-lg p-1.5 transition-all duration-200 ${
            isDownvoted
              ? 'text-downvote bg-downvote/15 shadow-sm shadow-downvote/20'
              : 'text-text-tertiary hover:text-downvote hover:bg-downvote/10 hover:translate-y-0.5 active:translate-y-0'
          }`}
        >
          <ArrowDown className={`h-4 w-4 transition-transform duration-200 ${isDownvoted ? 'fill-current scale-110' : ''}`} />
        </button>

        <div className="mx-1 h-4 w-px bg-border-primary/50" />

        {/* Comments */}
        <Link
          to={`/post/${post.id}#comments`}
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 flex items-center gap-1 rounded-lg px-2 py-1.5 text-text-tertiary transition-all duration-200 hover:bg-bg-tertiary hover:text-text-primary active:scale-95"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="text-xs font-semibold tabular-nums">{post.commentCount}</span>
        </Link>

        <div className="flex-1" />

        {/* UI-089: Direct WhatsApp + Copy Link buttons */}
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            trackShare('whatsapp', post.id, post.title, post.category, post.source?.id);
            trackShareButtonClick(post.id, 'whatsapp', 'feed_grid');
          }}
          title="Share on WhatsApp"
          aria-label="Share on WhatsApp"
          className="relative z-10 rounded-lg p-2 text-[#25D366] transition-all duration-200 hover:bg-[#25D366]/10 hover:scale-110 active:scale-95"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
        <button
          onClick={handleCopyLink}
          title={copied ? 'Copied!' : 'Copy link'}
          aria-label={copied ? 'Link copied' : 'Copy link'}
          className="relative z-10 rounded-lg p-2 text-text-tertiary transition-all duration-200 hover:bg-primary-500/10 hover:text-primary-400 hover:scale-110 active:scale-95"
        >
          {copied ? <Check className="h-4 w-4 text-green-400" /> : <Link2 className="h-4 w-4" />}
        </button>

        {/* Bookmark */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            bookmarkMutation.mutate();
          }}
          className={`relative z-10 rounded-lg p-2 transition-all duration-200 ${
            isBookmarked
              ? 'text-bookmark bg-bookmark/15 shadow-sm shadow-bookmark/20'
              : 'text-text-tertiary hover:text-bookmark hover:bg-bookmark/10 hover:scale-110 active:scale-95'
          }`}
        >
          <Bookmark className={`h-4 w-4 transition-all duration-200 ${isBookmarked ? 'fill-current' : ''}`} />
        </button>

        {/* External Link. T-209: an original's url is the /article/ marker,
            which no route serves — outboundUrl() returns null and we render
            nothing. The card itself already navigates to /post/<id>. */}
        {outboundUrl(post) && (
        <a
          href={outboundUrl(post)!}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 rounded-lg p-2 text-text-tertiary transition-all duration-200 hover:text-primary-400 hover:bg-primary-500/10 hover:scale-110 active:scale-95"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        )}
      </div>
    </article>
  );
}
